/**
 * Browser filter categories.
 *
 * The categories used to be a hardcoded constant inside `handlers/Browser.tsx`.
 * They are now configured per root organization in the Django admin and served
 * by `GET /contents/browse_filters/`.
 *
 * The endpoint always answers 200. There is no 404 path: an organization with
 * nothing configured gets `categories: []`, which means "render no filters" and
 * is a valid answer, not a failure. `DEFAULT_FILTER_CATEGORIES` is therefore
 * only used when the request itself fails or comes back unreadable.
 */

export type SupportedLanguage = 'es' | 'en';

/**
 * `es` and `en` are guaranteed by the backend's validation; other language
 * codes may also be present (`pt_br` and friends).
 */
export interface FilterCategoryLabel {
  es: string;
  en: string;
  [language: string]: string;
}

export interface FilterCategory {
  /** Stable code; this is what ends up in the `?filter=` URL param. */
  code: string;
  label: FilterCategoryLabel;
  keywords: string[];
}

/**
 * Ordered. The backend serves an array precisely so the order an admin writes
 * is the order the chips render in, so never turn this into an object: JS
 * object keys reorder anything that looks like an integer.
 */
export type FilterCategories = FilterCategory[];

/**
 * Path appended to `VITE_API_URL`. Overridable through the env var if the
 * route ever moves, no code change required.
 */
export const FILTER_CATEGORIES_ENDPOINT =
  (import.meta.env.VITE_FILTER_CATEGORIES_ENDPOINT as string | undefined) ||
  '/contents/browse_filters/';

/**
 * The categories the frontend shipped with, kept as the fallback for a failed
 * or unreadable request. The backend seeds the same list through
 * `seed_browse_filters`, so a configured organization normally serves these
 * until an admin edits them.
 */
export const DEFAULT_FILTER_CATEGORIES: FilterCategories = [
  {
    code: 'C',
    label: {
      es: 'Comunicación',
      en: 'Communication',
    },
    keywords: [
      'comunica',
      'adapta',
      'mensaje',
      'colabora',
      'redacción',
      'influencia',
      'comprensión',
      'claridad',
      'cambio',
      'stakeholders',
      'feedback',
      'expectativas',
      'cultura',
    ],
  },
  {
    code: 'L',
    label: {
      es: 'Liderazgo',
      en: 'Leadership',
    },
    keywords: [
      'cambio',
      'estrategia',
      'innovación',
      'adaptabilidad',
      'adaptación',
      'influencia',
      'inspiración',
      'visión',
      'rendimiento',
      'liderazgo',
      'estratégica',
      'coaching',
      'resultados',
      'tiempo',
      'alineación',
      'negocio',
    ],
  },
  {
    code: 'P',
    label: {
      es: 'Proyectos',
      en: 'Projects',
    },
    keywords: [
      'proyecto',
      'programas',
      'tiempo',
      'recursos',
      'negocio',
      'necesidades',
      'stakeholders',
      'cambio',
      'PM',
      'project',
    ],
  },
  {
    code: 'N',
    label: {
      es: 'Negociación',
      en: 'Negotiation',
    },
    keywords: [
      'estrategia',
      'acuerdo',
      'negocia',
      'escenario',
      'alternativ',
      'influencia',
      'persua',
      'adapta',
      'clima',
      'contexto',
      'impacto',
      'problema',
    ],
  },
  {
    code: 'I',
    label: {
      es: 'IT & PoCs',
      en: 'IT & PoCs',
    },
    keywords: [
      'innovación',
      'innovation',
      'estrategia',
      'cambio',
      'futuro',
      'crecimiento',
      'inspiración',
      'metodo',
      'agil',
      'lean',
      'disrup',
      'digital',
      'poc',
      'valida',
      'mvp',
      'idea',
      'data',
      'dato',
      'analytics',
      'ETL',
      'machine learning',
      'ML',
      'dama',
      'personalización',
      'cloud',
      'google',
      'nube',
      'sql',
      'big',
      'DL',
    ],
  },
  {
    code: 'O',
    label: {
      es: 'Coaching',
      en: 'Coaching',
    },
    keywords: [
      'inspira',
      'motiva',
      'mentor',
      'tiempo',
      'personas',
      'conflicto',
      'coach',
      'empatía',
      'cultura',
      'adapta',
      'expecta',
      'crisis',
      'bienestar',
      'confia',
    ],
  },
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The backend guarantees `{ es, en }` and may add more languages. Also accepts
 * a plain string and a partial label so a hand-authored configuration that
 * slipped past validation renders something instead of a blank chip.
 */
const normalizeLabel = (raw: unknown): FilterCategoryLabel | null => {
  if (typeof raw === 'string') {
    const text = raw.trim();
    return text ? { es: text, en: text } : null;
  }

  if (!isPlainObject(raw)) return null;

  const translations: Record<string, string> = {};
  Object.entries(raw).forEach(([language, text]) => {
    if (typeof text !== 'string') return;
    const trimmed = text.trim();
    if (trimmed) translations[language] = trimmed;
  });

  const es = translations.es ?? '';
  const en = translations.en ?? '';

  if (!es && !en) return null;

  return { ...translations, es: es || en, en: en || es };
};

const normalizeKeywords = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];

  // The backend de-duplicates case-insensitively and keeps the authored
  // casing ('PM', 'ETL'). Mirror that, since matching lowercases anyway.
  const seen = new Set<string>();

  return raw.reduce<string[]>((keywords, keyword) => {
    if (typeof keyword !== 'string') return keywords;

    const trimmed = keyword.trim();
    if (!trimmed) return keywords;

    const fingerprint = trimmed.toLowerCase();
    if (seen.has(fingerprint)) return keywords;

    seen.add(fingerprint);
    keywords.push(trimmed);
    return keywords;
  }, []);
};

const normalizeCategory = (
  code: unknown,
  raw: unknown
): FilterCategory | null => {
  if (!isPlainObject(raw)) return null;

  const trimmedCode = typeof code === 'string' ? code.trim() : '';
  if (!trimmedCode) return null;

  const label = normalizeLabel(raw.label ?? raw.title ?? raw.name);
  if (!label) return null;

  return {
    code: trimmedCode,
    label,
    keywords: normalizeKeywords(raw.keywords),
  };
};

const ENVELOPE_KEYS = ['categories', 'results', 'data'] as const;

const readCode = (item: Record<string, unknown>): unknown => {
  const code = item.code ?? item.key ?? item.value ?? item.id;
  return typeof code === 'string' || code === undefined || code === null
    ? code
    : String(code);
};

/**
 * Turns the endpoint's response into an ordered `FilterCategories`.
 *
 * The live shape, `GET /contents/browse_filters/`:
 *   `{ "organization_level": {...}, "is_default": false, "updated_at": "...",
 *      "categories": [{ "code": "C", "label": {...}, "keywords": [...] }] }`
 *   Everything outside `categories` is ignored; the filter bar has no use for
 *   it, and reading it would only create a second thing to keep in sync.
 *
 * Also accepts, so a backend tweak does not need a frontend release:
 *   - a bare array of entries, keyed by `code` (or `key` / `value` / `id`)
 *   - the bare map the frontend used before the endpoint existed,
 *     `{ "C": { "label": {...}, "keywords": [...] } }`, which the admin still
 *     accepts on input
 *   - `results` / `data` envelopes holding either of those
 *   - `label` as a plain string, or under `title` / `name`
 *
 * Returns `[]` for a well-formed empty taxonomy, which means "render no
 * filters" and is a real answer. Returns `null` only when the payload is not
 * readable at all, which is the caller's signal to fall back.
 */
export const normalizeFilterCategories = (
  raw: unknown
): FilterCategories | null => {
  if (isPlainObject(raw)) {
    const envelopeKey = ENVELOPE_KEYS.find((key) =>
      Object.prototype.hasOwnProperty.call(raw, key)
    );

    // Once it looks like an envelope, commit to it. Falling through to the
    // bare-map branch would read the envelope's own fields as categories:
    // `organization_level: { name: 'Acme Group' }` has a readable label and
    // would render an "Acme Group" chip.
    if (envelopeKey) {
      const wrapped = raw[envelopeKey];
      return wrapped === undefined || wrapped === raw
        ? null
        : normalizeFilterCategories(wrapped);
    }
  }

  if (!Array.isArray(raw) && !isPlainObject(raw)) return null;

  const entries: Array<[unknown, unknown]> = Array.isArray(raw)
    ? raw.map((item) =>
        isPlainObject(item) ? [readCode(item), item] : [undefined, item]
      )
    : Object.entries(raw);

  // A well-formed empty taxonomy. A bare `{}` carries no such signal, so it
  // falls through to the unreadable branch below.
  if (Array.isArray(raw) && entries.length === 0) return [];

  const categories: FilterCategories = [];
  const seenCodes = new Set<string>();

  entries.forEach(([code, value]) => {
    const category = normalizeCategory(code, value);
    if (!category) return;

    // Codes are unique case-insensitively on the backend. If a duplicate
    // arrives anyway, the first one wins: a repeated code would render two
    // chips that filter identically.
    const fingerprint = category.code.toLowerCase();
    if (seenCodes.has(fingerprint)) return;

    seenCodes.add(fingerprint);
    categories.push(category);
  });

  // Entries came in but none survived: the payload is malformed, not empty.
  return categories.length > 0 ? categories : null;
};

/**
 * Lookup by code, for the `?filter=` URL param. A linear scan over at most 50
 * categories, and unlike an object index it cannot be fooled by a code that
 * collides with a prototype member (`?filter=constructor`).
 */
export const getFilterCategory = (
  categories: FilterCategories,
  code: string
): FilterCategory | undefined =>
  categories.find((category) => category.code === code);

/**
 * Same rule the hardcoded version used: substring match, case insensitive,
 * against the content's `capacity` string.
 */
export const matchesFilterCategory = (
  category: FilterCategory | undefined,
  capacity: unknown
): boolean => {
  if (!category || !Array.isArray(category.keywords)) return false;
  if (typeof capacity !== 'string') return false;

  const capacityLower = capacity.toLowerCase();
  return category.keywords.some(
    (keyword) =>
      typeof keyword === 'string' &&
      capacityLower.includes(keyword.toLowerCase())
  );
};

export const getCategoryLabel = (
  category: FilterCategory | undefined,
  language: SupportedLanguage
): string | undefined => category?.label?.[language];
